import React, { useMemo } from 'react';
import { Button, OptionCard } from '@/components';
import { Link, Flex, Container, BREAKPOINTS, ExternalLink } from '@/ui';
import CreatePersonalIcon from '@/assets/entry-pages/create-personal.svg';
import { useTranslation } from 'react-i18next';
import infoIcon from '@/assets/icons/info.svg';
import styled from 'styled-components';
import { Tooltip, Typography } from 'antd';
import { COLORS } from '@/utils/static';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { useGetStatics } from '@/hooks/useGetStatics';

export const AgentRegStart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: statics } = useGetStatics();

  const emailStatic = useMemo(
    () => statics && statics.find((item) => item.title === 'email'),
    [statics]
  );

  return (
    <StyledMainRow vertical align='center' justify='center'>
      <StyledContainer>
        <Button
          size='large'
          type='text'
          text={t('Back')}
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
        />
      </StyledContainer>

      <StyledTextRow align={'center'} gap={8} $marginBottom={37}>
        <Container $marginTop={'-5'}>
          <StyledTitle2 level={2}>
            {t('Create your own agent account in a few minutes!')}
          </StyledTitle2>
        </Container>
        <Tooltip
          placement='topLeft'
          arrow={{ pointAtCenter: true }}
          title={t(
            'According to the established legislation, the service is available only to adults and legally competent individuals.'
          )}
        >
          <p>
            <Img src={infoIcon} />
          </p>
        </Tooltip>
      </StyledTextRow>

      <StyledOptionsRow gap={64} $marginBottom={48} justify={'center'} wrap>
        <OptionCard
          title={t('Create agent \naccount')}
          className={'col-auto'}
          onClick={() => {
            navigate('/registration/agent/1', { state: { account: 'AGENT' } });
          }}
          icon={CreatePersonalIcon}
        />
      </StyledOptionsRow>

      <Flex align={'center'} $marginBottom={4} $marginLeft={14}>
        <Container $marginRight={5}>
          <Text>{t('Already have an account?')}</Text>
        </Container>
        <Container>
          <Link to={'/login'}>
            <Button type='text' size='large' text={t('Log in')} />
          </Link>
        </Container>
      </Flex>

      <SmallText>
        {t('If you have any questions, please contact us at')} —{' '}
        <ExternalLink
          href={`mailto:${emailStatic && emailStatic?.text ? emailStatic.text : ''}`}
          external
        >
          {emailStatic && emailStatic?.text ? emailStatic.text : ''}
        </ExternalLink>
      </SmallText>
    </StyledMainRow>
  );
};

const StyledMainRow = styled(Flex)`
  min-height: 100vh;
  min-height: 100dvh;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    padding-top: 70px;
  }
`;

const StyledContainer = styled(Container)`
  position: absolute;
  top: 60px;
  left: 60px;
  width: min-content;
  z-index: 129;
  @media (max-width: ${BREAKPOINTS.md}px) {
    top: 15px;
    left: 15px;
  }
`;

const Img = styled.img`
  width: 24px;
  height: 24px;
`;

const StyledTitle2 = styled(Typography.Title)`
  line-height: normal;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    font-size: 18px;
    white-space: pre-wrap;
  }
`;

const Text = styled(Typography.Text)`
  font-size: 18px;
  color: ${COLORS.greyMedium};
`;

const SmallText = styled(Typography.Text)`
  font-size: 12px;
  color: ${COLORS.greyMedium};
`;

const StyledOptionsRow = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    gap: 24px;
  }
`;

const StyledTextRow = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin-bottom: 12px;
  }
`;
